import { useEffect, useMemo, useState, type FormEvent } from 'react';
import { adminApi, ApiError, type AdminPack, type AdminQuestion } from '@/lib/api';
import { useAdminToken } from '@/hooks/use-admin-token';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Checkbox } from '@/components/ui/checkbox';
import { Slider } from '@/components/ui/slider';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';

/**
 * Page admin /admin : gestion des questions par pack.
 *
 * Flow :
 *   1. Pas de token en sessionStorage -> ecran de login (input + bouton)
 *   2. Token valide -> sidebar packs + liste questions du pack selectionne
 *      + form "Ajouter une question" + actions edit/delete par question
 *
 * Securite :
 *   - Token envoye en header Authorization Bearer a chaque appel
 *   - Si 401 sur n'importe quel appel, on clear le token et retombe sur le login
 *   - Pas d'AgeGate sur cette page (admin = adulte par definition)
 */
export function AdminPage() {
  const { token, setToken, clearToken } = useAdminToken();
  const [packs, setPacks] = useState<AdminPack[]>([]);
  const [selectedPackSlug, setSelectedPackSlug] = useState<string | null>(null);
  const [questions, setQuestions] = useState<AdminQuestion[]>([]);
  const [loadingPacks, setLoadingPacks] = useState(false);
  const [loadingQuestions, setLoadingQuestions] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Filtres locaux sur la liste questions (pas de refetch necessaire)
  const [intensityFilter, setIntensityFilter] = useState<'ALL' | 1 | 2 | 3 | 4 | 5>('ALL');
  const [sourceFilter, setSourceFilter] = useState<'ALL' | 'YAML' | 'ADMIN'>('ALL');

  // Modale d'export YAML
  const [exportOpen, setExportOpen] = useState(false);

  // Recharge la liste des packs quand le token change ou au mount initial
  useEffect(() => {
    if (!token) {
      setPacks([]);
      setQuestions([]);
      setSelectedPackSlug(null);
      return;
    }
    setLoadingPacks(true);
    setError(null);
    adminApi
      .listPacks(token)
      .then((res) => {
        setPacks(res.packs);
        if (res.packs.length > 0 && !selectedPackSlug) {
          setSelectedPackSlug(res.packs[0]?.slug ?? null);
        }
      })
      .catch((err) => handleApiError(err))
      .finally(() => setLoadingPacks(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  // Recharge les questions quand le pack selectionne change
  useEffect(() => {
    if (!token || !selectedPackSlug) {
      setQuestions([]);
      return;
    }
    refreshQuestions();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token, selectedPackSlug]);

  function handleApiError(err: unknown) {
    if (err instanceof ApiError) {
      if (err.status === 401) {
        clearToken();
        setError('Token invalide ou expiré. Reconnecte-toi.');
        return;
      }
      setError(err.message);
    } else {
      setError('Erreur réseau');
    }
  }

  async function refreshQuestions() {
    if (!token || !selectedPackSlug) return;
    setLoadingQuestions(true);
    setError(null);
    try {
      const res = await adminApi.listQuestions(token, selectedPackSlug);
      setQuestions(res.questions);
    } catch (err) {
      handleApiError(err);
    } finally {
      setLoadingQuestions(false);
    }
  }

  // Filtre la liste questions selon intensite + source (pas de refetch)
  const filteredQuestions = useMemo(() => {
    return questions.filter((q) => {
      if (intensityFilter !== 'ALL' && q.intensity !== intensityFilter) return false;
      if (sourceFilter !== 'ALL' && q.source !== sourceFilter) return false;
      return true;
    });
  }, [questions, intensityFilter, sourceFilter]);

  if (!token) {
    return <LoginScreen onLogin={setToken} />;
  }

  return (
    <div className="max-w-5xl mx-auto space-y-6">
      <header className="flex items-center justify-between gap-3">
        <div>
          <h1 className="text-2xl font-bold text-primary">Admin Ember</h1>
          <p className="text-xs text-muted-foreground">
            Gère les questions par pack. Les questions ADMIN survivent aux re-seeds.
          </p>
        </div>
        <Button variant="outline" size="sm" onClick={clearToken}>
          Déconnexion
        </Button>
      </header>

      {error && (
        <Card className="border-destructive/40 bg-destructive/5">
          <CardContent className="pt-4 text-sm text-destructive" role="alert">
            {error}
          </CardContent>
        </Card>
      )}

      <div className="grid grid-cols-1 md:grid-cols-[200px_1fr] gap-6">
        <aside className="space-y-2">
          <p className="text-xs uppercase tracking-wider text-muted-foreground">Packs</p>
          {loadingPacks && <p className="text-sm text-muted-foreground">Chargement...</p>}
          {packs.map((pack) => (
            <button
              key={pack.slug}
              type="button"
              onClick={() => setSelectedPackSlug(pack.slug)}
              className={`w-full text-left rounded-md border px-3 py-2 transition-colors min-h-[44px] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring ${
                pack.slug === selectedPackSlug
                  ? 'border-primary bg-primary/10 text-primary'
                  : 'border-border bg-background/40 hover:border-primary/50'
              }`}
            >
              <span className="block text-sm font-medium">{pack.name}</span>
              <span className="block text-xs text-muted-foreground">
                {pack.questionCount} questions
              </span>
            </button>
          ))}
        </aside>

        <main className="space-y-6">
          {selectedPackSlug ? (
            <>
              <QuestionForm
                packSlug={selectedPackSlug}
                token={token}
                onCreated={refreshQuestions}
                onError={handleApiError}
              />

              <section className="space-y-3">
                <div className="flex items-center justify-between flex-wrap gap-2">
                  <h2 className="text-lg font-semibold">
                    Questions ({filteredQuestions.length}
                    {filteredQuestions.length !== questions.length && ` / ${questions.length}`})
                  </h2>
                  <div className="flex items-center gap-2">
                    {loadingQuestions && (
                      <span className="text-xs text-muted-foreground">Chargement...</span>
                    )}
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setExportOpen(true)}
                      disabled={questions.length === 0}
                    >
                      Exporter YAML
                    </Button>
                  </div>
                </div>

                <FiltersBar
                  intensity={intensityFilter}
                  onIntensityChange={setIntensityFilter}
                  source={sourceFilter}
                  onSourceChange={setSourceFilter}
                />

                {filteredQuestions.length === 0 && !loadingQuestions && (
                  <p className="text-sm text-muted-foreground italic">
                    {questions.length === 0
                      ? 'Aucune question dans ce pack.'
                      : 'Aucune question ne correspond aux filtres.'}
                  </p>
                )}
                <div className="space-y-2">
                  {filteredQuestions.map((q) => (
                    <QuestionRow
                      key={q.id}
                      question={q}
                      token={token}
                      onUpdated={refreshQuestions}
                      onDeleted={refreshQuestions}
                      onError={handleApiError}
                    />
                  ))}
                </div>
              </section>

              {exportOpen && (
                <ExportModal
                  token={token}
                  packSlug={selectedPackSlug}
                  onClose={() => setExportOpen(false)}
                  onError={handleApiError}
                />
              )}
            </>
          ) : (
            <p className="text-sm text-muted-foreground italic">Sélectionne un pack à gauche.</p>
          )}
        </main>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Login screen
// ---------------------------------------------------------------------------

function LoginScreen({ onLogin }: { onLogin: (token: string) => void }) {
  const [value, setValue] = useState('');

  function handleSubmit(e: FormEvent) {
    e.preventDefault();
    const trimmed = value.trim();
    if (trimmed.length === 0) return;
    onLogin(trimmed);
  }

  return (
    <div className="max-w-md mx-auto">
      <Card>
        <CardHeader>
          <CardTitle>Accès admin Ember</CardTitle>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="admin-token">Token admin</Label>
              <Input
                id="admin-token"
                type="password"
                value={value}
                onChange={(e) => setValue(e.target.value)}
                placeholder="ADMIN_TOKEN du .env"
                autoFocus
              />
              <p className="text-xs text-muted-foreground">
                Token statique défini côté serveur (variable d'environnement ADMIN_TOKEN).
              </p>
            </div>
            <Button
              type="submit"
              className="w-full min-h-[44px]"
              disabled={value.trim().length === 0}
            >
              Entrer
            </Button>
          </form>
        </CardContent>
      </Card>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Formulaire ajout question
// ---------------------------------------------------------------------------

interface QuestionFormProps {
  packSlug: string;
  token: string;
  onCreated: () => void;
  onError: (err: unknown) => void;
}

function QuestionForm({ packSlug, token, onCreated, onError }: QuestionFormProps) {
  const [text, setText] = useState('');
  const [intensity, setIntensity] = useState(3);
  const [optIns, setOptIns] = useState({ sex: false, emotion: false, dare: false });
  const [submitting, setSubmitting] = useState(false);

  async function handleSubmit(e: FormEvent) {
    e.preventDefault();
    const trimmed = text.trim();
    if (trimmed.length < 3) return;
    setSubmitting(true);
    try {
      await adminApi.createQuestion(token, {
        packSlug,
        text: trimmed,
        intensity,
        optIns,
      });
      setText('');
      setIntensity(3);
      setOptIns({ sex: false, emotion: false, dare: false });
      onCreated();
    } catch (err) {
      onError(err);
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-base">Nouvelle question</CardTitle>
      </CardHeader>
      <CardContent>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="new-text">Texte de la question</Label>
            <textarea
              id="new-text"
              value={text}
              onChange={(e) => setText(e.target.value)}
              rows={2}
              maxLength={500}
              placeholder="Ex: Décris la première fois que tu as..."
              className="w-full rounded-md border border-input bg-input/40 px-3 py-2 text-sm placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring resize-none"
            />
          </div>

          <div className="space-y-2">
            <Label>Intensité : {intensity}</Label>
            <Slider
              value={[intensity]}
              onValueChange={(v) => v[0] !== undefined && setIntensity(v[0])}
              min={1}
              max={5}
              step={1}
            />
          </div>

          <div className="space-y-2">
            <Label>Tags</Label>
            <div className="flex flex-wrap gap-4">
              <TagToggle
                label="Sex"
                checked={optIns.sex}
                onChange={(v) => setOptIns({ ...optIns, sex: v })}
              />
              <TagToggle
                label="Émotion"
                checked={optIns.emotion}
                onChange={(v) => setOptIns({ ...optIns, emotion: v })}
              />
              <TagToggle
                label="Défi (Dare)"
                checked={optIns.dare}
                onChange={(v) => setOptIns({ ...optIns, dare: v })}
              />
            </div>
          </div>

          <Button
            type="submit"
            className="w-full min-h-[44px]"
            disabled={submitting || text.trim().length < 3}
          >
            {submitting ? 'Ajout...' : 'Ajouter la question'}
          </Button>
        </form>
      </CardContent>
    </Card>
  );
}

function TagToggle({
  label,
  checked,
  onChange,
}: {
  label: string;
  checked: boolean;
  onChange: (v: boolean) => void;
}) {
  return (
    <label className="flex items-center gap-2 cursor-pointer">
      <Checkbox checked={checked} onCheckedChange={(state) => onChange(state === true)} />
      <span className="text-sm">{label}</span>
    </label>
  );
}

// ---------------------------------------------------------------------------
// Ligne question : affichage + edit inline + delete
// ---------------------------------------------------------------------------

interface QuestionRowProps {
  question: AdminQuestion;
  token: string;
  onUpdated: () => void;
  onDeleted: () => void;
  onError: (err: unknown) => void;
}

function QuestionRow({ question, token, onUpdated, onDeleted, onError }: QuestionRowProps) {
  const [editing, setEditing] = useState(false);
  const [text, setText] = useState(question.text);
  const [intensity, setIntensity] = useState(question.intensity);
  const [optIns, setOptIns] = useState({ ...question.optIns });
  const [busy, setBusy] = useState(false);

  function cancel() {
    setText(question.text);
    setIntensity(question.intensity);
    setOptIns({ ...question.optIns });
    setEditing(false);
  }

  async function save() {
    setBusy(true);
    try {
      await adminApi.updateQuestion(token, question.id, { text, intensity, optIns });
      setEditing(false);
      onUpdated();
    } catch (err) {
      onError(err);
    } finally {
      setBusy(false);
    }
  }

  async function remove() {
    if (!window.confirm(`Supprimer cette question ?\n\n"${question.text.slice(0, 80)}..."`)) {
      return;
    }
    setBusy(true);
    try {
      await adminApi.deleteQuestion(token, question.id);
      onDeleted();
    } catch (err) {
      onError(err);
    } finally {
      setBusy(false);
    }
  }

  if (editing) {
    return (
      <Card className="border-primary/40">
        <CardContent className="pt-4 space-y-3">
          <textarea
            value={text}
            onChange={(e) => setText(e.target.value)}
            rows={2}
            maxLength={500}
            className="w-full rounded-md border border-input bg-input/40 px-3 py-2 text-sm resize-none focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
          />
          <div className="space-y-2">
            <Label>Intensité : {intensity}</Label>
            <Slider
              value={[intensity]}
              onValueChange={(v) => v[0] !== undefined && setIntensity(v[0])}
              min={1}
              max={5}
              step={1}
            />
          </div>
          <div className="flex flex-wrap gap-4">
            <TagToggle
              label="Sex"
              checked={optIns.sex}
              onChange={(v) => setOptIns({ ...optIns, sex: v })}
            />
            <TagToggle
              label="Émotion"
              checked={optIns.emotion}
              onChange={(v) => setOptIns({ ...optIns, emotion: v })}
            />
            <TagToggle
              label="Défi"
              checked={optIns.dare}
              onChange={(v) => setOptIns({ ...optIns, dare: v })}
            />
          </div>
          <div className="flex gap-2 justify-end">
            <Button variant="outline" size="sm" onClick={cancel} disabled={busy}>
              Annuler
            </Button>
            <Button size="sm" onClick={save} disabled={busy || text.trim().length < 3}>
              {busy ? 'Enregistre...' : 'Enregistrer'}
            </Button>
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardContent className="pt-4 space-y-2">
        <div className="flex items-start justify-between gap-3">
          <p className="text-sm flex-1">{question.text}</p>
          <span
            className={`text-[10px] uppercase tracking-wider px-2 py-0.5 rounded-full ${
              question.source === 'ADMIN'
                ? 'bg-primary/20 text-primary'
                : 'bg-muted text-muted-foreground'
            }`}
          >
            {question.source}
          </span>
        </div>
        <div className="flex items-center gap-3 text-xs text-muted-foreground flex-wrap">
          <span>Intensité {question.intensity}</span>
          {question.optIns.sex && <span>· sex</span>}
          {question.optIns.emotion && <span>· émotion</span>}
          {question.optIns.dare && <span>· défi</span>}
          {question.usageCount > 0 && (
            <span className="text-primary">· jouée {question.usageCount}x</span>
          )}
        </div>
        <div className="flex gap-2 justify-end pt-1">
          <Button variant="outline" size="sm" onClick={() => setEditing(true)} disabled={busy}>
            Éditer
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={remove}
            disabled={busy || question.usageCount > 0}
            title={
              question.usageCount > 0
                ? 'Question jouée dans une partie, suppression refusée'
                : undefined
            }
          >
            Supprimer
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}

// ---------------------------------------------------------------------------
// Barre de filtres : intensite + source
// ---------------------------------------------------------------------------

interface FiltersBarProps {
  intensity: 'ALL' | 1 | 2 | 3 | 4 | 5;
  onIntensityChange: (v: 'ALL' | 1 | 2 | 3 | 4 | 5) => void;
  source: 'ALL' | 'YAML' | 'ADMIN';
  onSourceChange: (v: 'ALL' | 'YAML' | 'ADMIN') => void;
}

function FiltersBar({ intensity, onIntensityChange, source, onSourceChange }: FiltersBarProps) {
  const intensityOptions: Array<'ALL' | 1 | 2 | 3 | 4 | 5> = ['ALL', 1, 2, 3, 4, 5];
  const sourceOptions: Array<'ALL' | 'YAML' | 'ADMIN'> = ['ALL', 'YAML', 'ADMIN'];

  return (
    <div className="flex flex-wrap items-center gap-4 rounded-md border border-border bg-background/40 px-3 py-2">
      <div className="flex items-center gap-2">
        <span className="text-xs uppercase tracking-wider text-muted-foreground">Intensité</span>
        <div className="flex gap-1">
          {intensityOptions.map((opt) => (
            <FilterChip
              key={String(opt)}
              label={opt === 'ALL' ? 'Tout' : String(opt)}
              active={intensity === opt}
              onClick={() => onIntensityChange(opt)}
            />
          ))}
        </div>
      </div>
      <div className="flex items-center gap-2">
        <span className="text-xs uppercase tracking-wider text-muted-foreground">Source</span>
        <div className="flex gap-1">
          {sourceOptions.map((opt) => (
            <FilterChip
              key={opt}
              label={opt === 'ALL' ? 'Tout' : opt}
              active={source === opt}
              onClick={() => onSourceChange(opt)}
            />
          ))}
        </div>
      </div>
    </div>
  );
}

function FilterChip({
  label,
  active,
  onClick,
}: {
  label: string;
  active: boolean;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-pressed={active}
      className={`rounded-md border px-2.5 py-1 text-xs font-medium transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring ${
        active
          ? 'border-primary bg-primary/15 text-primary'
          : 'border-border bg-background/40 text-muted-foreground hover:border-primary/50'
      }`}
    >
      {label}
    </button>
  );
}

// ---------------------------------------------------------------------------
// Modale d'export YAML
// ---------------------------------------------------------------------------

interface ExportModalProps {
  token: string;
  packSlug: string;
  onClose: () => void;
  onError: (err: unknown) => void;
}

function ExportModal({ token, packSlug, onClose, onError }: ExportModalProps) {
  const [source, setSource] = useState<'ADMIN' | 'ALL'>('ADMIN');
  const [yamlText, setYamlText] = useState<string>('');
  const [loading, setLoading] = useState(false);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    setLoading(true);
    setCopied(false);
    adminApi
      .exportPack(token, packSlug, source)
      .then((text) => setYamlText(text))
      .catch((err) => {
        onError(err);
        setYamlText('');
      })
      .finally(() => setLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token, packSlug, source]);

  async function copyToClipboard() {
    try {
      await navigator.clipboard.writeText(yamlText);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      // Fallback ancien navigateur : on selectionne le textarea
      const el = document.getElementById('export-yaml-textarea') as HTMLTextAreaElement | null;
      if (el) {
        el.select();
        document.execCommand('copy');
        setCopied(true);
        setTimeout(() => setCopied(false), 2000);
      }
    }
  }

  function download() {
    const blob = new Blob([yamlText], { type: 'text/yaml;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `pack-${packSlug}-${source.toLowerCase()}.yaml`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  }

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-labelledby="export-modal-title"
      className="fixed inset-0 z-50 flex items-center justify-center bg-background/95 backdrop-blur-sm p-4"
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <Card className="max-w-2xl w-full max-h-[90vh] flex flex-col">
        <CardHeader className="flex-shrink-0">
          <div className="flex items-baseline justify-between gap-3">
            <CardTitle id="export-modal-title">Exporter le pack en YAML</CardTitle>
            <Button variant="outline" size="sm" onClick={onClose}>
              Fermer
            </Button>
          </div>
          <p className="text-xs text-muted-foreground">
            Copie ce YAML dans <code>prisma/data/pack-{packSlug}.yaml</code> du repo Ember pour
            rapatrier les questions et les promouvoir en source YAML au prochain seed.
          </p>
        </CardHeader>
        <CardContent className="flex-1 overflow-hidden flex flex-col gap-3">
          <div className="flex items-center gap-2">
            <span className="text-xs uppercase tracking-wider text-muted-foreground">Inclure</span>
            <FilterChip
              label="Questions admin uniquement"
              active={source === 'ADMIN'}
              onClick={() => setSource('ADMIN')}
            />
            <FilterChip
              label="Toutes les questions"
              active={source === 'ALL'}
              onClick={() => setSource('ALL')}
            />
          </div>

          <textarea
            id="export-yaml-textarea"
            value={loading ? 'Chargement...' : yamlText}
            readOnly
            spellCheck={false}
            className="flex-1 min-h-[300px] w-full rounded-md border border-border bg-background/60 p-3 text-xs font-mono resize-none focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
          />

          <div className="flex flex-wrap gap-2 justify-end">
            <Button
              variant="outline"
              onClick={download}
              disabled={loading || yamlText.length === 0}
            >
              Télécharger
            </Button>
            <Button
              onClick={() => void copyToClipboard()}
              disabled={loading || yamlText.length === 0}
            >
              {copied ? 'Copié !' : 'Copier'}
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
